import type { JSX } from 'react';
import {
  Node,
  mergeAttributes,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type CSSProperties, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  GroupOutlined,
  MoreOutlined,
  LeftOutlined,
  RightOutlined,
  HolderOutlined,
  DownOutlined,
  ExpandOutlined,
  CompressOutlined,
  ReloadOutlined,
  WarningFilled,
  ImportOutlined,
} from '@ant-design/icons';
import { FORMULA_FUNCS, colIndexToLetters, makeEngine } from './fk-formula';
import { uid } from './fk-uid';
import { applyPasteMatrix, parseClipboardMatrix, type Matrix } from './fk-table-import';
import type { FkImportDialog } from './fk-table-import-dialog';

/**
 * FkTable — интерактивный блок-таблица (уровень Miro/Notion) для статей БЗ.
 * Поддерживает: типы полей (текст/число/выбор/дата/человек), добавление и
 * удаление строк и столбцов, переименование и смену типа колонки, скрытие
 * полей, сортировку, фильтр, группировку, перетаскивание строк (dnd-kit).
 *
 * Round-trip в .md: сериализуется в fenced-блок одной JSON-строкой:
 *   ```fk-table
 *   {"columns":[...],"rows":[...],"view":{...}}
 *   ```
 * markdown-it (installFkTableFence) парсит обратно в <div data-fk-table='…'>,
 * Tiptap (parseHTML) узнаёт ноду. Та же логика на бэке (wiki-markdown.service)
 * для читалки. Держать синхронно.
 */

export type FkFieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'status'
  | 'tags'
  | 'date'
  | 'person'
  | 'link';

export interface FkColumn {
  id: string;
  name: string;
  type: FkFieldType;
  options?: string[];
  hidden?: boolean;
  /** Ширина столбца в px (если задана пользователем перетаскиванием границы). */
  width?: number;
}
export interface FkRow {
  id: string;
  cells: Record<string, string>;
  /** Высота строки в px (если задана пользователем). */
  height?: number;
}
export interface FkView {
  sort?: { col: string; dir: 'asc' | 'desc' } | null;
  filters?: { col: string; op: 'contains' | 'eq' | 'neq'; value: string }[];
  groupBy?: string | null;
}
export interface FkTableData {
  columns: FkColumn[];
  rows: FkRow[];
  view?: FkView;
  /** Название таблицы (показывается в шапке блока). */
  title?: string;
  /** Свёрнута ли таблица (видна только шапка с названием). */
  collapsed?: boolean;
}

const FIELD_LABEL: Record<FkFieldType, string> = {
  text: 'Текст',
  number: 'Число',
  select: 'Выбор',
  multiselect: 'Множественный выбор',
  status: 'Статус',
  tags: 'Теги',
  date: 'Дата',
  person: 'Человек',
  link: 'Ссылка',
};

/** Палитра статус-пилюль (детерминированно по значению). */
const STATUS_COLORS = ['#FF6900', '#0096FF', '#7CB342', '#E53935', '#8E54A8', '#00897B', '#F9A825'];
function statusColor(v: string): string {
  let h = 0;
  for (let i = 0; i < v.length; i++) h = (h * 31 + v.charCodeAt(i)) >>> 0;
  return STATUS_COLORS[h % STATUS_COLORS.length];
}
function splitMulti(v: string): string[] {
  return (v || '').split(',').map((s) => s.trim()).filter(Boolean);
}


type FkResolve = (rowId: string, colId: string) => { text: string; error: boolean };

/**
 * Контекст «выбора ячеек для формулы» (как в Google Sheets): когда юзер
 * редактирует формулу (ячейка с `=`), клик по другой ячейке вставляет её
 * ссылку (A1) в формулу на месте каретки; shift-клик добавляет «:ref» → диапазон.
 *   begin/end — активная формула-ячейка регистрирует свой insert.
 *   pick — pickable-ячейка передаёт свою ссылку активной формуле.
 *   activeKey — ключ активной ячейки (rowId:colId) или null (pick-режим выключен).
 */
interface FormulaPickApi {
  activeKey: string | null;
  begin: (key: string, insert: (ref: string) => void) => void;
  end: (key: string) => void;
  pick: (ref: string, range: boolean) => void;
}
const FormulaPickCtx = createContext<FormulaPickApi | null>(null);

/**
 * Контекст «вставка нескольких ячеек из буфера» (как в Google Таблицах): ячейка
 * при вставке TSV/html-таблицы отдаёт матрицу гриду, а тот раскладывает её по
 * ячейкам вправо и вниз от текущей одним patch.
 */
interface CellPasteApi {
  paste: (rowId: string, colId: string, matrix: Matrix) => void;
}
const CellPasteCtx = createContext<CellPasteApi | null>(null);

/**
 * Ключ localStorage для персональных отметок статусов (на пользователя/браузер).
 * Стабилен, пока не меняются столбцы и название таблицы.
 */
function tableKeyOf(data: FkTableData): string {
  const base = data.columns.map((c) => c.id).join(',') + '|' + (data.title || '');
  let h = 0;
  for (let i = 0; i < base.length; i++) h = (h * 31 + base.charCodeAt(i)) >>> 0;
  return 'fkov_' + h.toString(36);
}

function emptyData(): FkTableData {
  const c1 = uid('c');
  const c2 = uid('c');
  const c3 = uid('c');
  return {
    columns: [
      { id: c1, name: 'Название', type: 'text' },
      { id: c2, name: 'Статус', type: 'select', options: ['Не начато', 'В работе', 'Готово'] },
      { id: c3, name: 'Дата', type: 'date' },
    ],
    rows: [
      { id: uid('r'), cells: {} },
      { id: uid('r'), cells: {} },
    ],
    view: { sort: null, filters: [], groupBy: null },
  };
}

export function parseData(raw: unknown): FkTableData {
  try {
    const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (d && Array.isArray(d.columns) && Array.isArray(d.rows)) {
      return {
        columns: d.columns,
        rows: d.rows.map((r: FkRow) => ({ id: r.id || uid('r'), cells: r.cells || {}, height: r.height })),
        view: d.view || { sort: null, filters: [], groupBy: null },
        title: typeof d.title === 'string' ? d.title : '',
        collapsed: !!d.collapsed,
      };
    }
  } catch {
    /* ignore */
  }
  return emptyData();
}

/**
 * Slash-команда «Таблица из Excel / CSV»: вставляется обычная таблица, а её
 * NodeView сразу открывает диалог импорта. Это локальный одноразовый флаг
 * модуля, а НЕ атрибут ноды: атрибуты синхронизируются через Yjs (диалог
 * открылся бы у всех соавторов) и могли бы попасть в сохранённый текст статьи.
 */
let pendingImportAt = 0;
export function requestFkTableImport(): void {
  pendingImportAt = Date.now();
}
function takePendingImport(): boolean {
  const fresh = pendingImportAt > 0 && Date.now() - pendingImportAt < 3000;
  pendingImportAt = 0;
  return fresh;
}

// ─────────────────────────── Tiptap Node ───────────────────────────

export const FkTable = Node.create({
  name: 'fkTable',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      data: {
        default: null,
        parseHTML: (el: HTMLElement) => {
          const raw = el.getAttribute('data-fk-table');
          return raw ? parseData(raw) : emptyData();
        },
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-fk-table]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const data: FkTableData = node.attrs.data || emptyData();
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-fk-table': JSON.stringify(data),
        class: 'fk-table-block',
      }),
      // Статический fallback (для читалки до гидрации) — список колонок.
      ['div', { class: 'fk-table-fallback' }, `Таблица: ${data.columns.map((c) => c.name).join(', ')}`],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FkTableNodeView);
  },
});

function FkTableNodeView(props: NodeViewProps): JSX.Element {
  const data: FkTableData = props.node.attrs.data || emptyData();
  // Только что вставленная командой «Таблица из Excel / CSV» — открыть импорт.
  const [autoImport, setAutoImport] = useState(false);
  useEffect(() => {
    if (takePendingImport()) setAutoImport(true);
  }, []);
  return (
    <NodeViewWrapper className="fk-table-nvw" style={{ margin: '14px 0' }}>
      <div contentEditable={false}>
        <FkTableGrid
          data={data}
          editable
          selected={props.selected}
          autoOpenImport={autoImport}
          onChange={(next) => props.updateAttributes({ data: next })}
          onDeleteBlock={() => props.deleteNode()}
        />
      </div>
    </NodeViewWrapper>
  );
}

/**
 * Просмотр таблицы в читалке статьи: ячейки read-only, но фильтр/сортировка/
 * группировка/скрытие работают локально (без сохранения). Монтируется в
 * [data-fk-table] из ArticlePage.
 */
export function FkTableViewer({ raw }: { raw: string }): JSX.Element {
  const [data, setData] = useState<FkTableData>(() => parseData(raw));
  return <FkTableGrid data={data} editable={false} onChange={setData} />;
}

// ─────────────────────────── View logic ───────────────────────────

function applyView(data: FkTableData): FkRow[] {
  let rows = [...data.rows];
  const v = data.view || {};
  for (const f of v.filters || []) {
    if (!f.value && f.op !== 'eq') continue;
    rows = rows.filter((r) => {
      const cell = (r.cells[f.col] ?? '').toString().toLowerCase();
      const val = (f.value ?? '').toLowerCase();
      if (f.op === 'contains') return cell.includes(val);
      if (f.op === 'eq') return cell === val;
      if (f.op === 'neq') return cell !== val;
      return true;
    });
  }
  if (v.sort?.col) {
    const col = data.columns.find((c) => c.id === v.sort!.col);
    const num = col?.type === 'number';
    rows.sort((a, b) => {
      const x = a.cells[v.sort!.col] ?? '';
      const y = b.cells[v.sort!.col] ?? '';
      let cmp: number;
      if (num) cmp = (parseFloat(x) || 0) - (parseFloat(y) || 0);
      else cmp = x.toString().localeCompare(y.toString(), 'ru');
      return v.sort!.dir === 'asc' ? cmp : -cmp;
    });
  }
  return rows;
}

// ─────────────────────────── Popover (портал) ───────────────────────────
/**
 * Всплывающее меню поверх всего (портал в body, fixed-позиция у якоря).
 * Не клиппится контейнером (важно для полноэкранного режима). Закрывается
 * по клику вне меню и по Escape.
 */
function Popover({ anchor, onClose, align, children }: {
  anchor: DOMRect;
  onClose: () => void;
  align?: 'left' | 'right';
  children: ReactNode;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const MENU_W = 232;
  const left = align === 'right'
    ? Math.max(8, Math.min(anchor.right - MENU_W, window.innerWidth - MENU_W - 8))
    : Math.max(8, Math.min(anchor.left, window.innerWidth - MENU_W - 8));

  // Позиционируем после монтирования с учётом реальной высоты меню: если вниз
  // не влезает (упирается в низ экрана) — разворачиваем вверх над якорем; если
  // и так не влезает — прижимаем к низу. Плюс maxHeight+скролл как страховка,
  // чтобы нижние пункты («Удалить») всегда были доступны.
  const popRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number>(() => Math.min(anchor.bottom + 4, window.innerHeight - 60));
  useLayoutEffect(() => {
    const h = popRef.current?.offsetHeight ?? 180;
    const vh = window.innerHeight;
    let t = anchor.bottom + 4;
    if (t + h > vh - 8) {
      const up = anchor.top - h - 4;
      t = up >= 8 ? up : Math.max(8, vh - h - 8);
    }
    setTop(t);
  }, [anchor.top, anchor.bottom]);

  return createPortal(
    <>
      <div className="fk-tbl-backdrop" onMouseDown={onClose} />
      <div
        ref={popRef}
        className="fk-tbl-pop"
        style={{ top, left, width: MENU_W, maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ─────────────────────────── Confirm dialog (портал, по центру) ───────────────────────────
/** Красивое центрированное окно подтверждения (вместо window.confirm). */
function ConfirmDialog({ title, message, confirmText, danger, onConfirm, onCancel }: {
  title: string;
  message: string;
  confirmText: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, onConfirm]);

  return createPortal(
    <div className="fk-tbl-modal-overlay" onMouseDown={onCancel}>
      <div className="fk-tbl-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="fk-tbl-modal-body">
          <div className="fk-tbl-modal-icon"><WarningFilled /></div>
          <div className="fk-tbl-modal-text">
            <div className="fk-tbl-modal-title">{title}</div>
            <div className="fk-tbl-modal-msg">{message}</div>
          </div>
        </div>
        <div className="fk-tbl-modal-actions">
          <button type="button" className="fk-tbl-modal-btn" onMouseDown={(e) => { e.preventDefault(); onCancel(); }}>Отмена</button>
          <button type="button" className={`fk-tbl-modal-btn fk-tbl-modal-btn-primary${danger ? ' fk-tbl-modal-btn-danger' : ''}`} autoFocus onMouseDown={(e) => { e.preventDefault(); onConfirm(); }}>{confirmText}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────── Grid component ───────────────────────────

export function FkTableGrid({
  data,
  editable,
  selected,
  autoOpenImport,
  onChange,
  onDeleteBlock,
}: {
  data: FkTableData;
  editable: boolean;
  selected?: boolean;
  /** Открыть диалог импорта сразу после появления блока (команда «Таблица из Excel / CSV»). */
  autoOpenImport?: boolean;
  onChange: (next: FkTableData) => void;
  onDeleteBlock?: () => void;
}): JSX.Element {
  const [menuCol, setMenuCol] = useState<string | null>(null);
  const [menuColAnchor, setMenuColAnchor] = useState<DOMRect | null>(null);
  const [menuRow, setMenuRow] = useState<string | null>(null);
  const [menuRowAnchor, setMenuRowAnchor] = useState<DOMRect | null>(null);
  const [panel, setPanel] = useState<'filter' | 'sort' | 'group' | 'hide' | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // Диалог импорта; importMatrix — данные, вставленные в ячейку, но не влезшие в лимиты.
  const [importOpen, setImportOpen] = useState(false);
  const [importMatrix, setImportMatrix] = useState<Matrix | null>(null);
  useEffect(() => {
    if (autoOpenImport && editable) setImportOpen(true);
  }, [autoOpenImport, editable]);
  // Окно импорта нужно только редактору — подгружаем отдельным чанком при первом открытии,
  // чтобы не утяжелять читалку статей.
  const [ImportDialog, setImportDialog] = useState<typeof FkImportDialog | null>(null);
  useEffect(() => {
    if (!importOpen || ImportDialog) return undefined;
    let alive = true;
    import('./fk-table-import-dialog')
      .then((m) => { if (alive) setImportDialog(() => m.FkImportDialog); })
      .catch(() => {
        if (!alive) return;
        setImportOpen(false);
        window.alert('Не удалось загрузить окно импорта. Обновите страницу и попробуйте ещё раз.');
      });
    return () => { alive = false; };
  }, [importOpen, ImportDialog]);
  const openColMenu = (id: string, rect: DOMRect) => { setMenuCol(id); setMenuColAnchor(rect); };
  const openRowMenu = (id: string, rect: DOMRect) => { setMenuRow(id); setMenuRowAnchor(rect); };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const collapsed = !!data.collapsed;

  const view = data.view || {};
  const visibleCols = data.columns.filter((c) => !c.hidden);
  const displayRows = useMemo(() => applyView(data), [data]);

  // ── Персональные отметки статусов (режим просмотра) ──
  // Каждый пользователь в режиме чтения может выбрать свой статус в ячейках
  // типа «Выбор»/«Статус». Хранится локально (localStorage), виден только ему.
  const tableKey = useMemo(() => tableKeyOf(data), [data.columns, data.title]);
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>({});
  const hasOverrides = Object.values(overrides).some((r) => Object.keys(r).length > 0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Загрузка: сначала локальный кэш (мгновенно), затем серверные данные (источник
  // правды, синхронизация между устройствами). localStorage — оффлайн-фолбэк.
  useEffect(() => {
    if (editable) return;
    let alive = true;
    try {
      const raw = localStorage.getItem(tableKey);
      if (raw) setOverrides(JSON.parse(raw));
    } catch { /* ignore */ }
    fetch(`/api/wiki/table-state/${encodeURIComponent(tableKey)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (!alive || !res) return;
        const data = (res.data || {}) as Record<string, Record<string, string>>;
        setOverrides(data);
        try { localStorage.setItem(tableKey, JSON.stringify(data)); } catch { /* ignore */ }
      })
      .catch(() => { /* нет сети/аноним — остаёмся на localStorage */ });
    return () => { alive = false; };
  }, [editable, tableKey]);
  const pushToServer = (next: Record<string, Record<string, string>>) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`/api/wiki/table-state/${encodeURIComponent(tableKey)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: next }),
      }).catch(() => { /* оффлайн — данные в localStorage, синхронизируются позже */ });
    }, 500);
  };
  const setOverride = (rowId: string, colId: string, value: string) => {
    setOverrides((prev) => {
      const next = { ...prev, [rowId]: { ...(prev[rowId] || {}), [colId]: value } };
      try { localStorage.setItem(tableKey, JSON.stringify(next)); } catch { /* ignore */ }
      pushToServer(next);
      return next;
    });
  };
  const [confirmReset, setConfirmReset] = useState(false);
  const doResetOverrides = () => {
    try { localStorage.removeItem(tableKey); } catch { /* ignore */ }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    fetch(`/api/wiki/table-state/${encodeURIComponent(tableKey)}`, { method: 'DELETE', credentials: 'include' })
      .catch(() => { /* оффлайн */ });
    setOverrides({});
    setConfirmReset(false);
  };
  const getOverride = (rowId: string, colId: string): string | undefined => overrides[rowId]?.[colId];

  // Движок формул (пересоздаётся при изменении столбцов/строк).
  const resolve = useMemo<FkResolve>(() => makeEngine(data.columns, data.rows), [data.columns, data.rows]);
  // Буквы столбцов (A, B, C…) и номера строк — для адресации в формулах.
  const colLetter = (id: string) => colIndexToLetters(data.columns.findIndex((c) => c.id === id));
  const rowNumOf = (id: string) => data.rows.findIndex((r) => r.id === id) + 1;
  /** A1-ссылка ячейки (буква столбца + номер строки) — для вставки в формулу кликом. */
  const cellRefOf = (rowId: string, colId: string) => colLetter(colId) + rowNumOf(rowId);

  // Pick-режим формул: активная формула-ячейка регистрирует insert; клик по
  // другой ячейке вставляет её ссылку.
  const pickRef = useRef<{ key: string; insert: (r: string) => void } | null>(null);
  const [pickActiveKey, setPickActiveKey] = useState<string | null>(null);
  const formulaPick = useMemo<FormulaPickApi>(
    () => ({
      activeKey: pickActiveKey,
      begin: (key, insert) => { pickRef.current = { key, insert }; setPickActiveKey(key); },
      end: (key) => { if (pickRef.current?.key === key) { pickRef.current = null; setPickActiveKey(null); } },
      pick: (ref, range) => { pickRef.current?.insert(range ? ':' + ref : ref); },
    }),
    [pickActiveKey],
  );

  const patch = (p: Partial<FkTableData>) => onChange({ ...data, ...p });
  const setView = (v: Partial<FkView>) => patch({ view: { ...view, ...v } });

  // ── мутации структуры ──
  const addRow = () => patch({ rows: [...data.rows, { id: uid('r'), cells: {} }] });
  const delRow = (id: string) => patch({ rows: data.rows.filter((r) => r.id !== id) });
  const addRowAt = (id: string, where: 'above' | 'below') => {
    const idx = data.rows.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const at = where === 'above' ? idx : idx + 1;
    const next = data.rows.slice();
    next.splice(at, 0, { id: uid('r'), cells: {} });
    patch({ rows: next });
  };
  const duplicateRow = (id: string) => {
    const idx = data.rows.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const next = data.rows.slice();
    next.splice(idx + 1, 0, { id: uid('r'), cells: { ...data.rows[idx].cells } });
    patch({ rows: next });
  };
  const addColumn = () => {
    const id = uid('c');
    patch({ columns: [...data.columns, { id, name: `Поле ${data.columns.length + 1}`, type: 'text' }] });
  };
  const updateColumn = (id: string, p: Partial<FkColumn>) =>
    patch({ columns: data.columns.map((c) => (c.id === id ? { ...c, ...p } : c)) });
  const delColumn = (id: string) =>
    patch({
      columns: data.columns.filter((c) => c.id !== id),
      rows: data.rows.map((r) => {
        const { [id]: _drop, ...rest } = r.cells;
        return { ...r, cells: rest };
      }),
    });
  const moveColumn = (id: string, dir: -1 | 1) => {
    const idx = data.columns.findIndex((c) => c.id === id);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= data.columns.length) return;
    patch({ columns: arrayMove(data.columns, idx, to) });
  };
  const setCell = (rowId: string, colId: string, value: string) =>
    patch({
      rows: data.rows.map((r) => (r.id === rowId ? { ...r, cells: { ...r.cells, [colId]: value } } : r)),
    });
  const setRowHeight = (rowId: string, height: number) =>
    patch({ rows: data.rows.map((r) => (r.id === rowId ? { ...r, height } : r)) });

  const onRowDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = data.rows.findIndex((r) => r.id === active.id);
    const to = data.rows.findIndex((r) => r.id === over.id);
    if (from < 0 || to < 0) return;
    patch({ rows: arrayMove(data.rows, from, to) });
  };
  const onColDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = data.columns.findIndex((c) => c.id === active.id);
    const to = data.columns.findIndex((c) => c.id === over.id);
    if (from < 0 || to < 0) return;
    patch({ columns: arrayMove(data.columns, from, to) });
  };

  // группировка
  const groups = useMemo(() => {
    if (!view.groupBy) return [{ key: '__all', label: '', rows: displayRows }];
    const map = new Map<string, FkRow[]>();
    for (const r of displayRows) {
      const k = r.cells[view.groupBy] || '—';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return [...map.entries()].map(([key, rows]) => ({ key, label: key, rows }));
  }, [displayRows, view.groupBy]);

  const grouped = !!view.groupBy;

  // Вставка нескольких ячеек из буфера (TSV / html-таблица) с текущей ячейки вправо
  // и вниз, одним patch. Порядок строк/полей — как их видит пользователь
  // (сортировка, фильтр, группировка, скрытые поля). Не влезло в лимиты — открываем
  // диалог импорта с этими данными и предупреждением.
  const pasteRef = useRef<CellPasteApi['paste']>(() => {});
  pasteRef.current = (rowId, colId, matrix) => {
    const res = applyPasteMatrix(data, { rowId, colId }, matrix, {
      visibleRowIds: groups.flatMap((g) => g.rows.map((r) => r.id)),
      visibleColIds: visibleCols.map((c) => c.id),
      uid,
    });
    if (res.ok) patch({ columns: res.data.columns, rows: res.data.rows });
    else if (res.reason === 'limits') { setImportMatrix(matrix); setImportOpen(true); }
  };
  const pasteApi = useMemo<CellPasteApi>(() => ({ paste: (r, c, m) => pasteRef.current(r, c, m) }), []);

  return (
   <FormulaPickCtx.Provider value={formulaPick}>
   <CellPasteCtx.Provider value={pasteApi}>
    <div
      className={`fk-tbl${fullscreen ? ' fk-tbl--fs' : ''}`}
      style={fullscreen ? undefined : { border: selected ? '2px solid var(--fk-orange)' : '1px solid var(--fk-border,#e6e6ea)' }}
    >
      {/* Шапка блока: свернуть/развернуть · название · на весь экран */}
      <div className="fk-tbl-head">
        <button type="button" className="fk-tbl-chev" title={collapsed ? 'Развернуть' : 'Свернуть'} onMouseDown={(e) => { e.preventDefault(); patch({ collapsed: !collapsed }); }}>
          {collapsed ? <RightOutlined /> : <DownOutlined />}
        </button>
        {editable ? (
          <input
            className="fk-tbl-title"
            placeholder="Название таблицы"
            value={data.title || ''}
            onChange={(e) => patch({ title: e.target.value })}
          />
        ) : (
          <span className="fk-tbl-title fk-tbl-title-ro">{data.title || 'Таблица'}</span>
        )}
        <span style={{ flex: 1 }} />
        {!editable && (
          <button type="button" className="fk-tbl-chev" title="Сбросить мои отметки статусов" disabled={!hasOverrides} onMouseDown={(e) => { e.preventDefault(); setConfirmReset(true); }}>
            <ReloadOutlined />
          </button>
        )}
        {editable && (
          <button type="button" className="fk-tbl-tbtn" title="Загрузить данные из Excel или CSV, либо вставить из Google Таблиц" onMouseDown={(e) => { e.preventDefault(); setImportMatrix(null); setImportOpen(true); }}>
            <ImportOutlined /> Импорт
          </button>
        )}
        <button type="button" className="fk-tbl-chev" title={fullscreen ? 'Свернуть из полноэкранного' : 'На весь экран'} onMouseDown={(e) => { e.preventDefault(); setFullscreen((v) => !v); }}>
          {fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
        </button>
      </div>

      {!collapsed && (
        <>
      {/* Тулбар (фильтр/сортировка/группировка/скрытие — работает и в просмотре) */}
      {(
        <div className="fk-tbl-toolbar">
          <button type="button" className="fk-tbl-tbtn" data-on={panel === 'filter'} onMouseDown={(e) => { e.preventDefault(); setPanel(panel === 'filter' ? null : 'filter'); }}>
            <FilterOutlined /> Фильтр{(view.filters?.length ?? 0) > 0 ? ` (${view.filters!.length})` : ''}
          </button>
          <button type="button" className="fk-tbl-tbtn" data-on={panel === 'sort'} onMouseDown={(e) => { e.preventDefault(); setPanel(panel === 'sort' ? null : 'sort'); }}>
            <SortAscendingOutlined /> Сортировка
          </button>
          <button type="button" className="fk-tbl-tbtn" data-on={panel === 'group'} onMouseDown={(e) => { e.preventDefault(); setPanel(panel === 'group' ? null : 'group'); }}>
            <GroupOutlined /> Группировка
          </button>
          <button type="button" className="fk-tbl-tbtn" data-on={panel === 'hide'} onMouseDown={(e) => { e.preventDefault(); setPanel(panel === 'hide' ? null : 'hide'); }}>
            <EyeInvisibleOutlined /> Скрыть поля
          </button>
          <span style={{ flex: 1 }} />
          {onDeleteBlock && (
            <button type="button" className="fk-tbl-tbtn" data-danger onMouseDown={(e) => { e.preventDefault(); onDeleteBlock(); }}>
              <DeleteOutlined /> Удалить
            </button>
          )}
        </div>
      )}

      {panel && (
        <div className="fk-tbl-panel">
          {panel === 'filter' && (
            <FilterPanel data={data} onChange={(filters) => setView({ filters })} />
          )}
          {panel === 'sort' && (
            <SortPanel data={data} onChange={(sort) => setView({ sort })} />
          )}
          {panel === 'group' && (
            <GroupPanel data={data} onChange={(groupBy) => setView({ groupBy })} />
          )}
          {panel === 'hide' && (
            <HidePanel data={data} onToggle={(id, hidden) => updateColumn(id, { hidden })} />
          )}
        </div>
      )}

      {/* Таблица */}
      <div className="fk-tbl-scroll">
        <table className="fk-tbl-grid">
          <thead>
            <tr>
              {editable && <th className="fk-tbl-gutter" />}
              {editable ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onColDragEnd}>
                  <SortableContext items={visibleCols.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
                    {visibleCols.map((col) => (
                      <SortableTh
                        key={col.id}
                        col={col}
                        letter={colLetter(col.id)}
                        onDblName={(rect) => openColMenu(col.id, rect)}
                        onResize={(width) => updateColumn(col.id, { width })}
                      >
                        <button type="button" className="fk-tbl-col-menu" onMouseDown={(e) => { e.preventDefault(); openColMenu(col.id, (e.currentTarget.closest('th') as HTMLElement).getBoundingClientRect()); }}>
                          <MoreOutlined />
                        </button>
                      </SortableTh>
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                visibleCols.map((col) => (
                  <th key={col.id} className="fk-tbl-th" style={col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : undefined}><span className="fk-tbl-th-letter">{colLetter(col.id)}</span><span className="fk-tbl-th-name">{col.name}</span></th>
                ))
              )}
              {editable && (
                <th className="fk-tbl-addcol">
                  <button type="button" title="Добавить поле" onMouseDown={(e) => { e.preventDefault(); addColumn(); }}>
                    <PlusOutlined />
                  </button>
                </th>
              )}
            </tr>
          </thead>

          {grouped ? (
            groups.map((g) => (
              <tbody key={g.key}>
                <tr className="fk-tbl-group-row">
                  <td colSpan={visibleCols.length + (editable ? 2 : 0)}>
                    <strong>{g.label}</strong> <span className="fk-tbl-group-count">{g.rows.length}</span>
                  </td>
                </tr>
                {g.rows.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    cols={visibleCols}
                    editable={editable}
                    onCell={setCell}
                    resolve={resolve}
                    num={rowNumOf(row.id)}
                    onMenu={(rect) => openRowMenu(row.id, rect)}
                    onRowResize={(h) => setRowHeight(row.id, h)}
                    getCellRef={(colId) => cellRefOf(row.id, colId)}
                    getOverride={(colId) => getOverride(row.id, colId)}
                    onOverride={(colId, v) => setOverride(row.id, colId, v)}
                  />
                ))}
              </tbody>
            ))
          ) : editable ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRowDragEnd}>
              <SortableContext items={displayRows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {displayRows.map((row) => (
                    <SortableRow
                      key={row.id}
                      row={row}
                      cols={visibleCols}
                      editable
                      onCell={setCell}
                      resolve={resolve}
                      num={rowNumOf(row.id)}
                      onMenu={(rect) => openRowMenu(row.id, rect)}
                      onRowResize={(h) => setRowHeight(row.id, h)}
                      getCellRef={(colId) => cellRefOf(row.id, colId)}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          ) : (
            <tbody>
              {displayRows.map((row) => (
                <Row key={row.id} row={row} cols={visibleCols} editable={false} onCell={setCell} resolve={resolve} num={rowNumOf(row.id)} getOverride={(colId) => getOverride(row.id, colId)} onOverride={(colId, v) => setOverride(row.id, colId, v)} />
              ))}
            </tbody>
          )}
        </table>
      </div>

      {editable && (
        <button type="button" className="fk-tbl-addrow" onMouseDown={(e) => { e.preventDefault(); addRow(); }}>
          <PlusOutlined /> Строка
        </button>
      )}
        </>
      )}

      {/* Меню столбца — поверх всего (портал) */}
      {editable && menuCol && menuColAnchor && (() => {
        const col = data.columns.find((c) => c.id === menuCol);
        if (!col) return null;
        return (
          <Popover anchor={menuColAnchor} align="right" onClose={() => setMenuCol(null)}>
            <ColumnMenu
              col={col}
              onClose={() => setMenuCol(null)}
              onRename={(name) => updateColumn(col.id, { name })}
              onType={(type) => updateColumn(col.id, { type })}
              onOptions={(options) => updateColumn(col.id, { options })}
              onHide={() => { updateColumn(col.id, { hidden: true }); setMenuCol(null); }}
              onDelete={() => { delColumn(col.id); setMenuCol(null); }}
              onSort={(dir) => { setView({ sort: { col: col.id, dir } }); setMenuCol(null); }}
              onGroup={() => { setView({ groupBy: col.id }); setMenuCol(null); }}
              onMoveLeft={() => moveColumn(col.id, -1)}
              onMoveRight={() => moveColumn(col.id, 1)}
            />
          </Popover>
        );
      })()}

      {/* Меню строки — поверх всего (портал) */}
      {editable && menuRow && menuRowAnchor && data.rows.some((r) => r.id === menuRow) && (
        <Popover anchor={menuRowAnchor} align="left" onClose={() => setMenuRow(null)}>
          <RowMenu
            onAddAbove={() => { addRowAt(menuRow, 'above'); setMenuRow(null); }}
            onAddBelow={() => { addRowAt(menuRow, 'below'); setMenuRow(null); }}
            onDuplicate={() => { duplicateRow(menuRow); setMenuRow(null); }}
            onDel={() => { delRow(menuRow); setMenuRow(null); }}
            onClose={() => setMenuRow(null)}
          />
        </Popover>
      )}

      {confirmReset && (
        <ConfirmDialog
          title="Сбросить мои отметки?"
          message="Ваши личные отметки статусов в этой таблице будут удалены. Базовые значения, заданные автором, останутся. Действие нельзя отменить."
          confirmText="Сбросить"
          danger
          onConfirm={doResetOverrides}
          onCancel={() => setConfirmReset(false)}
        />
      )}

      {editable && importOpen && ImportDialog && (
        <ImportDialog
          data={data}
          initialMatrix={importMatrix}
          typeLabels={FIELD_LABEL}
          onClose={() => { setImportOpen(false); setImportMatrix(null); }}
          onApply={(next) => {
            // Одно изменение → одна запись в историю/Yjs.
            patch({ columns: next.columns, rows: next.rows, view: next.view, collapsed: false });
            setImportOpen(false);
            setImportMatrix(null);
          }}
        />
      )}

      <style>{FK_TBL_CSS}</style>
    </div>
   </CellPasteCtx.Provider>
   </FormulaPickCtx.Provider>
  );
}

// ─────────────────────────── Rows ───────────────────────────

function Cell({ row, col, editable, onCell, resolve, override, onOverride }: { row: FkRow; col: FkColumn; editable: boolean; onCell: (r: string, c: string, v: string) => void; resolve: FkResolve; override?: string; onOverride?: (v: string) => void }): JSX.Element {
  const value = row.cells[col.id] ?? '';
  const set = (v: string) => onCell(row.id, col.id, v);
  const isFormula = value.startsWith('=');

  // ── Режим чтения ──
  if (!editable) {
    // Интерактивный личный статус: каждый пользователь выбирает своё значение в
    // ячейках «Выбор»/«Статус», оно сохраняется только у него (localStorage).
    if (onOverride && (col.type === 'select' || col.type === 'status') && !isFormula) {
      const cur = override ?? value;
      const mine = override !== undefined && override !== value;
      return (
        <span className="fk-tbl-vwrap">
          {col.type === 'status' && <span className="fk-tbl-vdot" style={{ background: cur ? statusColor(cur) : '#d8d6e0' }} />}
          <select
            className={`fk-tbl-input fk-tbl-vselect${mine ? ' fk-tbl-vselect-mine' : ''}`}
            value={cur}
            onChange={(e) => onOverride(e.target.value)}
            title={mine ? 'Ваша личная отметка (базовое: ' + (value || '—') + ')' : undefined}
          >
            <option value="">—</option>
            {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </span>
      );
    }
    if (!value) return <span />;
    if (isFormula) {
      const { text, error } = resolve(row.id, col.id);
      return <span className={error ? 'fk-tbl-fx-err' : 'fk-tbl-fx'} title={value}>{text}</span>;
    }
    if (col.type === 'select') return <span className="fk-tbl-chip">{value}</span>;
    if (col.type === 'status')
      return <span className="fk-tbl-status" style={{ ['--st' as string]: statusColor(value) }}>{value}</span>;
    if (col.type === 'multiselect' || col.type === 'tags')
      return <span className="fk-tbl-chips">{splitMulti(value).map((t) => <span key={t} className="fk-tbl-chip">{t}</span>)}</span>;
    if (col.type === 'link')
      return <a href={value} target="_blank" rel="noopener noreferrer" className="fk-tbl-link">{value}</a>;
    return <span>{value}</span>;
  }

  // ── Режим редактирования ──
  // Выпадающие типы (select/status/multiselect) формулы не поддерживают — там нельзя ввести «=».
  if (col.type === 'select' || col.type === 'status') {
    return (
      <span className={col.type === 'status' ? 'fk-tbl-cellwrap' : undefined}>
        <select className="fk-tbl-input" value={value} onChange={(e) => set(e.target.value)}>
          <option value="">—</option>
          {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </span>
    );
  }
  if (col.type === 'multiselect' || col.type === 'tags') {
    const listId = `dl-${col.id}`;
    return (
      <>
        <input
          className="fk-tbl-input"
          value={value}
          placeholder="через запятую"
          list={col.type === 'multiselect' ? listId : undefined}
          onChange={(e) => set(e.target.value)}
        />
        {col.type === 'multiselect' && (
          <datalist id={listId}>{(col.options || []).map((o) => <option key={o} value={o} />)}</datalist>
        )}
      </>
    );
  }
  // Текст/число/дата/ссылка/человек — поддерживают формулы (ввод «=»).
  return (
    <TextCell
      value={value}
      inputType={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : col.type === 'link' ? 'url' : 'text'}
      placeholder={col.type === 'link' ? 'https://…' : undefined}
      onChange={set}
      resolve={resolve}
      rowId={row.id}
      colId={col.id}
    />
  );
}

/**
 * Ячейка с поддержкой формул. При вводе «=» показывает подсказку функций
 * (топ-20) и живой предпросмотр результата; вне фокуса показывает вычисленное
 * значение (как в Excel — в ячейке результат, в строке формул — формула).
 */
function TextCell({ value, inputType, placeholder, onChange, resolve, rowId, colId }: {
  value: string;
  inputType: string;
  placeholder?: string;
  onChange: (v: string) => void;
  resolve: FkResolve;
  rowId: string;
  colId: string;
}): JSX.Element {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const isFormula = value.startsWith('=');

  // ── Pick-режим: пока редактируем формулу, регистрируемся как активная
  // ячейка; клик по другой ячейке вставит её A1-ссылку на месте каретки. ──
  const pickCtx = useContext(FormulaPickCtx);
  const pasteCtx = useContext(CellPasteCtx);
  const pickKey = `${rowId}:${colId}`;
  const insertImpl = (refStr: string): void => {
    const el = ref.current;
    const s = el?.selectionStart ?? value.length;
    const e = el?.selectionEnd ?? value.length;
    const nv = value.slice(0, s) + refStr + value.slice(e);
    onChange(nv);
    requestAnimationFrame(() => {
      const el2 = ref.current;
      if (el2) { el2.focus(); const p = s + refStr.length; el2.setSelectionRange(p, p); }
    });
  };
  const insertImplRef = useRef(insertImpl);
  insertImplRef.current = insertImpl;
  useEffect(() => {
    if (focused && isFormula && pickCtx) {
      pickCtx.begin(pickKey, (r) => insertImplRef.current(r));
      return () => pickCtx.end(pickKey);
    }
    return undefined;
  }, [focused, isFormula, pickCtx, pickKey]);

  // Слово, набираемое в конце формулы — по нему фильтруем подсказку функций.
  const fxQuery = isFormula && focused ? (/([A-Za-zА-Яа-я]+)$/.exec(value)?.[1] ?? '').toUpperCase() : null;
  const suggestions = fxQuery == null ? [] : FORMULA_FUNCS.filter((f) => f.name.startsWith(fxQuery)).slice(0, 7);

  const applyFunc = (name: string) => {
    const base = value.replace(/([A-Za-zА-Яа-я]+)$/, '');
    const nv = `${base}${name}()`;
    onChange(nv);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) { el.focus(); const pos = nv.length - 1; el.setSelectionRange(pos, pos); }
    });
  };

  const preview = isFormula ? resolve(rowId, colId) : null;

  return (
    <span className="fk-tbl-fxwrap">
      <input
        ref={ref}
        className="fk-tbl-input"
        type={isFormula ? 'text' : inputType}
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        style={isFormula && !focused ? { color: 'transparent' } : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value)}
        onPaste={(e) => {
          // Формула в ячейке — обычная вставка текста (не мешаем её редактированию).
          if (!pasteCtx || isFormula) return;
          // Несколько ячеек (TSV / html-таблица) раскладываем по сетке; одно значение — как раньше.
          const m = parseClipboardMatrix(e.clipboardData.getData('text/html'), e.clipboardData.getData('text/plain'));
          if (!m) return;
          e.preventDefault();
          pasteCtx.paste(rowId, colId, m);
        }}
      />
      {isFormula && !focused && preview && (
        <span className={`fk-tbl-fxoverlay${preview.error ? ' fk-tbl-fx-err' : ''}`}>{preview.text || '—'}</span>
      )}
      {isFormula && focused && (
        <span className="fk-tbl-fxpreview">{preview && preview.error ? preview.text : `= ${preview?.text ?? ''}`}</span>
      )}
      {suggestions.length > 0 && (
        <div className="fk-tbl-fxmenu">
          {suggestions.map((f) => (
            <button key={f.name} type="button" onMouseDown={(e) => { e.preventDefault(); applyFunc(f.name); }}>
              <span className="fk-tbl-fxmenu-name">{f.tpl}</span>
              <span className="fk-tbl-fxmenu-desc">{f.desc}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * Хук pick-режима для ячеек строки: даёт className подсветки и обработчик
 * mousedown, который вставляет A1-ссылку кликнутой ячейки в активную формулу
 * (shift — диапазон). Перехват на capture-фазе td — чтобы не уводить фокус с
 * редактируемой формулы.
 */
function useFormulaPickCell(rowId: string, getCellRef?: (colId: string) => string) {
  const ctx = useContext(FormulaPickCtx);
  const isPickable = (colId: string): boolean =>
    !!(getCellRef && ctx?.activeKey && ctx.activeKey !== `${rowId}:${colId}`);
  return {
    pickClass: (colId: string) => (isPickable(colId) ? ' fk-tbl-pickable' : ''),
    onPickDown: (e: ReactMouseEvent, colId: string) => {
      if (!isPickable(colId)) return;
      e.preventDefault();
      e.stopPropagation();
      ctx!.pick(getCellRef!(colId), e.shiftKey);
    },
  };
}

interface RowProps {
  row: FkRow;
  cols: FkColumn[];
  editable: boolean;
  onCell: (r: string, c: string, v: string) => void;
  resolve: FkResolve;
  num?: number;
  onMenu?: (rect: DOMRect) => void;
  onRowResize?: (height: number) => void;
  getOverride?: (colId: string) => string | undefined;
  onOverride?: (colId: string, v: string) => void;
  /** A1-ссылка ячейки для вставки в формулу кликом. */
  getCellRef?: (colId: string) => string;
}

/** Меню строки (как в Miro): открывается по клику на ⠿. */
function RowMenu(p: { onAddAbove?: () => void; onAddBelow?: () => void; onDuplicate?: () => void; onDel: () => void; onClose: () => void }): JSX.Element {
  return (
    <div className="fk-tbl-rowmenu">
      <button type="button" onMouseDown={(e) => { e.preventDefault(); p.onAddAbove?.(); }}>↑ Добавить строку выше</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); p.onAddBelow?.(); }}>↓ Добавить строку ниже</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); p.onDuplicate?.(); }}>⧉ Дублировать</button>
      <button type="button" data-danger onMouseDown={(e) => { e.preventDefault(); p.onDel(); }}>🗑 Удалить</button>
    </div>
  );
}

/** Перетаскивание нижней границы строки → изменение высоты. */
function rowResizeHandler(e: ReactPointerEvent, onRowResize?: (h: number) => void): void {
  if (!onRowResize) return;
  e.preventDefault();
  e.stopPropagation();
  const tr = (e.currentTarget as HTMLElement).closest('tr') as HTMLElement | null;
  if (!tr) return;
  const startY = e.clientY;
  const startH = tr.getBoundingClientRect().height;
  let last = startH;
  const move = (ev: PointerEvent) => {
    last = Math.max(32, startH + (ev.clientY - startY));
    tr.style.height = `${last}px`;
  };
  const up = () => {
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
    onRowResize(Math.round(last));
  };
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', up);
}

function cellStyle(col: FkColumn): CSSProperties | undefined {
  return col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : undefined;
}

/** Группированная строка (без drag): ⠿ только открывает меню. */
function Row(props: RowProps): JSX.Element {
  const { row, cols, editable, onCell, resolve } = props;
  const { pickClass, onPickDown } = useFormulaPickCell(row.id, props.getCellRef);
  return (
    <tr className="fk-tbl-row" style={row.height ? { height: row.height } : undefined}>
      {editable && (
        <td className="fk-tbl-gutter">
          <span className="fk-tbl-rownum">{props.num}</span>
          <button type="button" className="fk-tbl-drag" title="Меню строки" onMouseDown={(e) => { e.preventDefault(); props.onMenu?.((e.currentTarget.closest('td') as HTMLElement).getBoundingClientRect()); }}>
            <HolderOutlined />
          </button>
          <span className="fk-tbl-row-resize" title="Потянуть — высота строки" onPointerDown={(e) => rowResizeHandler(e, props.onRowResize)} />
        </td>
      )}
      {cols.map((col) => (
        <td key={col.id} className={`fk-tbl-td${pickClass(col.id)}`} style={cellStyle(col)} onMouseDownCapture={(e) => onPickDown(e, col.id)}><Cell row={row} col={col} editable={editable} onCell={onCell} resolve={resolve} override={props.getOverride?.(col.id)} onOverride={props.onOverride ? (v) => props.onOverride!(col.id, v) : undefined} /></td>
      ))}
      {editable && <td className="fk-tbl-endcell" />}
    </tr>
  );
}

/** Перетаскиваемая строка (dnd-kit): тянешь за ⠿ — перенос, клик — меню. */
function SortableRow(props: RowProps): JSX.Element {
  const { row, cols, onCell, resolve } = props;
  const { pickClass, onPickDown } = useFormulaPickCell(row.id, props.getCellRef);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1,
    ...(row.height ? { height: row.height } : {}),
  };
  return (
    <tr ref={setNodeRef} style={style} className="fk-tbl-row">
      <td className="fk-tbl-gutter">
        <span className="fk-tbl-rownum">{props.num}</span>
        <button
          type="button"
          className="fk-tbl-drag"
          title="Тянуть — перенос · клик — меню"
          {...attributes}
          {...listeners}
          onClick={(e) => props.onMenu?.((e.currentTarget.closest('td') as HTMLElement).getBoundingClientRect())}
        >
          <HolderOutlined />
        </button>
        <span className="fk-tbl-row-resize" title="Потянуть — высота строки" onPointerDown={(e) => rowResizeHandler(e, props.onRowResize)} />
      </td>
      {cols.map((col) => (
        <td key={col.id} className={`fk-tbl-td${pickClass(col.id)}`} style={cellStyle(col)} onMouseDownCapture={(e) => onPickDown(e, col.id)}><Cell row={row} col={col} editable onCell={onCell} resolve={resolve} /></td>
      ))}
      <td className="fk-tbl-endcell" />
    </tr>
  );
}

/** Перетаскивание правой границы заголовка → изменение ширины столбца. */
function colResizeHandler(e: ReactPointerEvent, onResize?: (w: number) => void): void {
  if (!onResize) return;
  e.preventDefault();
  e.stopPropagation();
  const th = (e.currentTarget as HTMLElement).closest('th') as HTMLElement | null;
  if (!th) return;
  const startX = e.clientX;
  const startW = th.getBoundingClientRect().width;
  let last = startW;
  const move = (ev: PointerEvent) => {
    last = Math.max(60, startW + (ev.clientX - startX));
    th.style.width = `${last}px`;
    th.style.minWidth = `${last}px`;
    th.style.maxWidth = `${last}px`;
  };
  const up = () => {
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
    onResize(Math.round(last));
  };
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', up);
}

/** Перетаскиваемый заголовок столбца (dnd-kit). Двойной клик по названию — меню; правый край — ресайз. */
function SortableTh({ col, children, letter, onDblName, onResize }: { col: FkColumn; children: ReactNode; letter?: string; onDblName?: (rect: DOMRect) => void; onResize?: (w: number) => void }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1,
    ...(col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : {}),
  };
  return (
    <th ref={setNodeRef} style={style} className="fk-tbl-th">
      <span className="fk-tbl-th-drag" title="Перетащить столбец" {...attributes} {...listeners}>
        <HolderOutlined />
      </span>
      {letter && <span className="fk-tbl-th-letter">{letter}</span>}
      <span
        className="fk-tbl-th-name"
        title="Двойной клик — меню/переименовать"
        onDoubleClick={(e) => onDblName?.((e.currentTarget.closest('th') as HTMLElement).getBoundingClientRect())}
      >
        {col.name}
      </span>
      {children}
      <span className="fk-tbl-col-resize" title="Потянуть — ширина столбца" onPointerDown={(e) => colResizeHandler(e, onResize)} />
    </th>
  );
}

// ─────────────────────────── Column menu & panels ───────────────────────────

function ColumnMenu(props: {
  col: FkColumn;
  onClose: () => void;
  onRename: (n: string) => void;
  onType: (t: FkFieldType) => void;
  onOptions: (o: string[]) => void;
  onHide: () => void;
  onDelete: () => void;
  onSort: (d: 'asc' | 'desc') => void;
  onGroup: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
}): JSX.Element {
  const { col } = props;
  return (
    <div className="fk-tbl-menu">
      <input
        className="fk-tbl-menu-name"
        defaultValue={col.name}
        autoFocus
        onBlur={(e) => props.onRename(e.target.value.trim() || col.name)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      />
      <label className="fk-tbl-menu-label">Тип поля</label>
      <select className="fk-tbl-menu-name" value={col.type} onChange={(e) => props.onType(e.target.value as FkFieldType)}>
        {(Object.keys(FIELD_LABEL) as FkFieldType[]).map((t) => (
          <option key={t} value={t}>{FIELD_LABEL[t]}</option>
        ))}
      </select>
      {(col.type === 'select' || col.type === 'status' || col.type === 'multiselect') && (
        <input
          className="fk-tbl-menu-name"
          placeholder="Варианты через запятую"
          defaultValue={(col.options || []).join(', ')}
          onBlur={(e) => props.onOptions(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        />
      )}
      <div className="fk-tbl-menu-row">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); props.onMoveLeft(); }}><LeftOutlined /> Левее</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); props.onMoveRight(); }}>Правее <RightOutlined /></button>
      </div>
      <button type="button" className="fk-tbl-menu-item" onMouseDown={(e) => { e.preventDefault(); props.onSort('asc'); }}>Сортировать ↑</button>
      <button type="button" className="fk-tbl-menu-item" onMouseDown={(e) => { e.preventDefault(); props.onSort('desc'); }}>Сортировать ↓</button>
      <button type="button" className="fk-tbl-menu-item" onMouseDown={(e) => { e.preventDefault(); props.onGroup(); }}>Группировать по полю</button>
      <button type="button" className="fk-tbl-menu-item" onMouseDown={(e) => { e.preventDefault(); props.onHide(); }}>Скрыть поле</button>
      <button type="button" className="fk-tbl-menu-item" data-danger onMouseDown={(e) => { e.preventDefault(); props.onDelete(); }}>Удалить поле</button>
    </div>
  );
}

function FilterPanel({ data, onChange }: { data: FkTableData; onChange: (f: FkView['filters']) => void }): JSX.Element {
  const filters = data.view?.filters || [];
  const add = () => onChange([...filters, { col: data.columns[0]?.id || '', op: 'contains', value: '' }]);
  const upd = (i: number, p: Partial<{ col: string; op: 'contains' | 'eq' | 'neq'; value: string }>) =>
    onChange(filters.map((f, idx) => (idx === i ? { ...f, ...p } : f)));
  const del = (i: number) => onChange(filters.filter((_, idx) => idx !== i));
  return (
    <div className="fk-tbl-panel-body">
      {filters.length === 0 && <div className="fk-tbl-muted">Фильтров пока нет</div>}
      {filters.map((f, i) => (
        <div key={i} className="fk-tbl-filter-row">
          <span>Где</span>
          <select value={f.col} onChange={(e) => upd(i, { col: e.target.value })}>
            {data.columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={f.op} onChange={(e) => upd(i, { op: e.target.value as 'contains' | 'eq' | 'neq' })}>
            <option value="contains">содержит</option>
            <option value="eq">равно</option>
            <option value="neq">не равно</option>
          </select>
          <input placeholder="значение" value={f.value} onChange={(e) => upd(i, { value: e.target.value })} />
          <button type="button" onMouseDown={(e) => { e.preventDefault(); del(i); }}><DeleteOutlined /></button>
        </div>
      ))}
      <button type="button" className="fk-tbl-add-link" onMouseDown={(e) => { e.preventDefault(); add(); }}><PlusOutlined /> Добавить фильтр</button>
    </div>
  );
}

function SortPanel({ data, onChange }: { data: FkTableData; onChange: (s: FkView['sort']) => void }): JSX.Element {
  const sort = data.view?.sort || null;
  return (
    <div className="fk-tbl-panel-body fk-tbl-filter-row">
      <span>Сортировать по</span>
      <select value={sort?.col || ''} onChange={(e) => onChange(e.target.value ? { col: e.target.value, dir: sort?.dir || 'asc' } : null)}>
        <option value="">— нет —</option>
        {data.columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select value={sort?.dir || 'asc'} disabled={!sort} onChange={(e) => sort && onChange({ col: sort.col, dir: e.target.value as 'asc' | 'desc' })}>
        <option value="asc">по возрастанию</option>
        <option value="desc">по убыванию</option>
      </select>
    </div>
  );
}

function GroupPanel({ data, onChange }: { data: FkTableData; onChange: (g: string | null) => void }): JSX.Element {
  const groupBy = data.view?.groupBy || '';
  return (
    <div className="fk-tbl-panel-body fk-tbl-filter-row">
      <span>Группировать по</span>
      <select value={groupBy} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">— нет —</option>
        {data.columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </div>
  );
}

function HidePanel({ data, onToggle }: { data: FkTableData; onToggle: (id: string, hidden: boolean) => void }): JSX.Element {
  return (
    <div className="fk-tbl-panel-body">
      {data.columns.map((c) => (
        <label key={c.id} className="fk-tbl-hide-row">
          <input type="checkbox" checked={!c.hidden} onChange={(e) => onToggle(c.id, !e.target.checked)} />
          {c.name}
        </label>
      ))}
    </div>
  );
}

const FK_TBL_CSS = `
.fk-tbl{border-radius:12px;background:#fff;overflow:hidden;font-size:13px}
.fk-tbl--fs{position:fixed;inset:24px;z-index:1000;border:1px solid var(--fk-border,#e6e6ea)!important;box-shadow:0 20px 60px rgba(20,20,40,.28);display:flex;flex-direction:column;overflow:auto}
.fk-tbl-head{display:flex;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid var(--fk-border,#e6e6ea);background:#fff}
.fk-tbl-chev{border:none;background:none;cursor:pointer;color:#8a8696;padding:3px;border-radius:6px;display:inline-flex}
.fk-tbl-chev:hover{background:var(--fk-bg-subtle,#f7f6fa);color:var(--fk-orange,#ff6900)}
.fk-tbl-chev:disabled{opacity:.35;cursor:default}
.fk-tbl-chev:disabled:hover{background:none;color:#8a8696}
.fk-tbl-td .fk-tbl-vwrap{display:flex;align-items:center;gap:6px;padding:4px 8px}
.fk-tbl-vdot{width:9px;height:9px;border-radius:50%;flex:0 0 auto}
.fk-tbl-vselect{border:1px solid var(--fk-border,#e6e6ea)!important;border-radius:6px;padding:3px 6px!important;background:#fff;cursor:pointer;width:auto;min-width:90px}
.fk-tbl-vselect-mine{border-color:var(--fk-orange,#ff6900)!important;color:var(--fk-orange,#ff6900);font-weight:600}
.fk-tbl-title{border:none;background:none;font-size:15px;font-weight:600;color:var(--fk-text,#1a1f36);padding:3px 4px;border-radius:6px;flex:0 1 auto;min-width:120px}
.fk-tbl-title:focus{outline:none;background:var(--fk-bg-subtle,#f7f6fa)}
.fk-tbl-title-ro{flex:0 1 auto}
.fk-tbl-toolbar{display:flex;align-items:center;gap:6px;padding:6px 8px;border-bottom:1px solid var(--fk-border,#e6e6ea);flex-wrap:wrap;background:var(--fk-bg-subtle,#f7f6fa)}
.fk-tbl-tbtn{display:inline-flex;align-items:center;gap:5px;border:1px solid transparent;background:transparent;border-radius:7px;padding:4px 9px;font-size:12px;color:#444;cursor:pointer}
.fk-tbl-tbtn:hover{background:#fff;border-color:var(--fk-border,#e6e6ea)}
.fk-tbl-tbtn[data-on="true"]{background:var(--fk-orange-soft,#fff3e8);color:var(--fk-orange,#ff6900)}
.fk-tbl-tbtn[data-danger]:hover{background:rgba(229,57,53,.1);color:var(--fk-error,#e53935)}
.fk-tbl-panel{padding:8px 10px;border-bottom:1px solid var(--fk-border,#e6e6ea);background:#fcfbfe}
.fk-tbl-panel-body{display:flex;flex-direction:column;gap:6px}
.fk-tbl-filter-row{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.fk-tbl-filter-row select,.fk-tbl-filter-row input,.fk-tbl-input,.fk-tbl-menu-name{border:1px solid var(--fk-border,#e6e6ea);border-radius:6px;padding:3px 6px;font-size:12px;background:#fff}
.fk-tbl-muted{color:#9b97ad;font-size:12px}
.fk-tbl-add-link{align-self:flex-start;display:inline-flex;align-items:center;gap:5px;border:none;background:none;color:var(--fk-orange,#ff6900);cursor:pointer;font-size:12px;padding:2px 0}
.fk-tbl-hide-row{display:flex;align-items:center;gap:7px;font-size:13px;cursor:pointer}
.fk-tbl-scroll{overflow-x:auto}
.fk-tbl--fs .fk-tbl-scroll{overflow:visible}
.fk-tbl-grid{border-collapse:collapse;width:auto}
.fk-tbl-grid th,.fk-tbl-grid td{border:1px solid var(--fk-border,#eceaf1);padding:0;text-align:left;vertical-align:middle}
.fk-tbl-th{position:relative;padding:7px 24px 7px 22px;background:var(--fk-bg-subtle,#f7f6fa);font-weight:600;color:var(--fk-text,#1a1f36);white-space:nowrap}
.fk-tbl-col-resize{position:absolute;right:0;top:0;bottom:0;width:7px;cursor:col-resize;z-index:6;touch-action:none}
.fk-tbl-col-resize:hover{background:var(--fk-orange,#ff6900);opacity:.45}
.fk-tbl-row-resize{position:absolute;left:0;right:0;bottom:-3px;height:6px;cursor:row-resize;z-index:6;opacity:0;transition:opacity .12s;touch-action:none}
.fk-tbl-row:hover .fk-tbl-row-resize{opacity:1}
.fk-tbl-row-resize:hover{background:var(--fk-orange,#ff6900);opacity:.45}
.fk-tbl-endcell{border:none!important;background:#fff;min-width:0;width:0;padding:0}
.fk-tbl-th-drag{position:absolute;left:4px;top:8px;cursor:grab;color:#c4c2cf;opacity:0;transition:opacity .12s}
.fk-tbl-th:hover .fk-tbl-th-drag{opacity:1}
.fk-tbl-th-drag:hover{color:var(--fk-orange,#ff6900)}
.fk-tbl-th-name{cursor:default}
.fk-tbl-th-letter{display:inline-block;min-width:15px;margin-right:5px;color:#b8b6c4;font-weight:600;font-size:10px;text-align:center}
.fk-tbl-col-menu{position:absolute;right:8px;top:5px;z-index:7;border:none;background:none;color:#9b97ad;cursor:pointer;padding:2px}
.fk-tbl-col-menu:hover{color:var(--fk-orange,#ff6900)}
.fk-tbl-gutter{position:relative;width:34px;height:34px;text-align:center;color:#c4c2cf;background:var(--fk-bg-subtle,#f7f6fa);white-space:nowrap}
.fk-tbl-rownum{color:#b8b6c4;font-size:11px;transition:opacity .12s}
.fk-tbl-row:hover .fk-tbl-rownum{opacity:0}
.fk-tbl-drag{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);border:none;background:none;cursor:grab;color:#b8b6c4;padding:2px;opacity:0;transition:opacity .12s}
.fk-tbl-row:hover .fk-tbl-drag{opacity:1}
.fk-tbl-drag:hover{color:var(--fk-orange,#ff6900)}
.fk-tbl-rowmenu{display:flex;flex-direction:column;gap:2px;text-align:left}
.fk-tbl-rowmenu button{text-align:left;border:none;background:none;padding:6px 8px;border-radius:6px;font-size:12px;cursor:pointer;color:#444}
.fk-tbl-rowmenu button:hover{background:var(--fk-bg-subtle,#f7f6fa)}
.fk-tbl-rowmenu button[data-danger]{color:var(--fk-error,#e53935)}
/* Всплывающее меню поверх всего (портал) */
.fk-tbl-backdrop{position:fixed;inset:0;z-index:1999}
.fk-tbl-pop{position:fixed;z-index:2000;background:#fff;border:1px solid var(--fk-border,#e6e6ea);border-radius:10px;box-shadow:0 12px 36px rgba(20,20,40,.2);padding:8px;display:flex;flex-direction:column;gap:5px;max-height:80vh;overflow:auto}
.fk-tbl-td{padding:0}
.fk-tbl-pickable{cursor:cell;box-shadow:inset 0 0 0 2px rgba(255,105,0,.30)}
.fk-tbl-pickable:hover{box-shadow:inset 0 0 0 2px #FF6900;background:rgba(255,105,0,.07)}
.fk-tbl-input{width:100%;border:none;border-radius:0;padding:7px 10px;background:transparent;font-size:13px}
.fk-tbl-input:focus{outline:2px solid var(--fk-orange,#ff6900);outline-offset:-2px}
.fk-tbl-td>span{display:inline-block;padding:7px 10px}
.fk-tbl-chip{display:inline-block;background:var(--fk-orange-soft,#fff3e8);color:var(--fk-orange,#ff6900);border-radius:6px;padding:2px 8px;font-size:12px}
.fk-tbl-chips{display:inline-flex;flex-wrap:wrap;gap:4px;padding:6px 10px}
.fk-tbl-status{display:inline-block;padding:2px 10px;border-radius:999px;font-size:12px;font-weight:600;color:#fff;background:var(--st,#888);margin:6px 10px}
.fk-tbl-link{display:inline-block;padding:7px 10px;color:var(--fk-info,#0096ff);text-decoration:underline;word-break:break-all}
.fk-tbl-addcol{width:40px;text-align:center}
.fk-tbl-addcol button,.fk-tbl-addrow{border:none;background:none;color:#9b97ad;cursor:pointer}
.fk-tbl-addrow{display:flex;align-items:center;gap:6px;padding:8px 12px;width:100%;color:var(--fk-text-2,#6b6b76);font-size:13px}
.fk-tbl-addrow:hover,.fk-tbl-addcol button:hover{color:var(--fk-orange,#ff6900)}
.fk-tbl-group-row td{background:#f3f1f8;padding:6px 12px;font-size:13px;color:var(--fk-text,#1a1f36)}
.fk-tbl-group-count{color:#9b97ad;margin-left:6px}
.fk-tbl-menu{display:flex;flex-direction:column;gap:5px}
.fk-tbl-menu-name{width:100%;box-sizing:border-box}
.fk-tbl-menu-label{font-size:11px;color:#9b97ad;margin-top:2px}
.fk-tbl-menu-row{display:flex;gap:5px}
.fk-tbl-menu-row button{flex:1;border:1px solid var(--fk-border,#e6e6ea);background:#fff;border-radius:6px;padding:4px;font-size:11px;cursor:pointer}
.fk-tbl-menu-item{text-align:left;border:none;background:none;padding:5px 6px;border-radius:6px;font-size:12px;cursor:pointer;color:#444}
.fk-tbl-menu-item:hover{background:var(--fk-bg-subtle,#f7f6fa)}
.fk-tbl-menu-item[data-danger]{color:var(--fk-error,#e53935)}
/* — Формулы — */
.fk-tbl-fx{display:inline-block;padding:7px 10px;font-variant-numeric:tabular-nums}
.fk-tbl-fx-err{display:inline-block;padding:7px 10px;color:var(--fk-error,#e53935);font-size:12px}
.fk-tbl-fxwrap{position:relative;display:block;width:100%;padding:0!important}
.fk-tbl-fxoverlay{position:absolute;inset:0;display:flex;align-items:center;padding:7px 10px;pointer-events:none;font-variant-numeric:tabular-nums;color:var(--fk-text,#1a1f36)}
.fk-tbl-fxoverlay.fk-tbl-fx-err{color:var(--fk-error,#e53935);font-size:12px}
.fk-tbl-fxpreview{position:absolute;left:0;right:0;top:100%;z-index:25;background:#251d4a;color:#fff;font-size:11px;padding:3px 8px;border-radius:0 0 6px 6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fk-tbl-fxmenu{position:absolute;left:0;top:100%;z-index:40;min-width:230px;max-height:240px;overflow:auto;background:#fff;border:1px solid var(--fk-border,#e6e6ea);border-radius:10px;box-shadow:0 8px 28px rgba(20,20,40,.18);padding:5px;margin-top:18px;display:flex;flex-direction:column;gap:1px}
.fk-tbl-fxmenu button{display:flex;flex-direction:column;align-items:flex-start;gap:1px;text-align:left;border:none;background:none;padding:5px 8px;border-radius:6px;cursor:pointer;width:100%}
.fk-tbl-fxmenu button:hover{background:var(--fk-orange-soft,#fff3e8)}
.fk-tbl-fxmenu-name{font-family:'SF Mono',Menlo,Consolas,monospace;font-size:12px;color:var(--fk-indigo,#251d4a);font-weight:600}
.fk-tbl-fxmenu-desc{font-size:11px;color:#9b97ad}
/* — Окно подтверждения (по центру) — */
.fk-tbl-modal-overlay{position:fixed;inset:0;z-index:3000;background:rgba(20,20,40,.5);display:flex;align-items:center;justify-content:center;padding:16px;animation:fkFade .12s ease}
.fk-tbl-modal{width:100%;max-width:460px;background:#fff;border-radius:22px;box-shadow:0 28px 80px rgba(20,20,40,.4);padding:28px 30px 22px;animation:fkPop .15s ease}
.fk-tbl-modal-body{display:flex;align-items:flex-start;gap:18px;margin-bottom:22px}
.fk-tbl-modal-icon{flex:0 0 auto;font-size:46px;color:#f7c023;line-height:1;margin-top:2px}
.fk-tbl-modal-text{flex:1;min-width:0}
.fk-tbl-modal-title{font-size:22px;font-weight:800;color:#1a1430;margin-bottom:10px;letter-spacing:-.01em}
.fk-tbl-modal-msg{font-size:15px;line-height:1.5;color:#6b6878}
.fk-tbl-modal-actions{display:flex;justify-content:flex-end;gap:12px}
.fk-tbl-modal-btn{border:1.5px solid var(--fk-border,#e6e6ea);background:#fff;border-radius:12px;padding:11px 26px;font-size:15px;font-weight:700;color:#1a1430;cursor:pointer;outline:none;transition:background .12s,border-color .12s,box-shadow .12s}
.fk-tbl-modal-btn:focus,.fk-tbl-modal-btn:focus-visible{outline:none}
.fk-tbl-modal-btn:hover{background:var(--fk-bg-subtle,#f7f6fa)}
.fk-tbl-modal-btn-primary{border-color:var(--fk-orange,#ff6900);background:var(--fk-orange,#ff6900);color:#fff}
.fk-tbl-modal-btn-primary:hover{background:#e85f00;border-color:#e85f00}
.fk-tbl-modal-btn-danger{border-color:#ed3b34;background:#ed3b34;color:#fff;box-shadow:0 6px 16px rgba(237,59,52,.32)}
.fk-tbl-modal-btn-danger:hover{background:#d92e27;border-color:#d92e27}
@keyframes fkFade{from{opacity:0}to{opacity:1}}
@keyframes fkPop{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}
.fk-table-fallback{display:none}
`;
